/**
 * OperationsFieldTeam.tsx — Operations / Agents (M7b): the field-team page.
 *
 * The mockup's agent-profile-card grid (klaxiora-crm-mockup-full.html
 * :1721-1820) over real data: the active roster (GET /v1/hr/employees
 * ?status=active — the shipped picker population) joined with ONE
 * month-windowed task fetch, so each card's "Tasks this month" figure is
 * true (fieldTeamView owns the join and the month bounds). The AI-agent
 * AgentsList this route rendered before M7b is untouched and stays
 * reachable at the dormant automation.agents alias (Platform section,
 * SHOW_PLATFORM_SECTION — unlinked, not gone).
 *
 * Per-section degrade: the roster is the page (LoadingState/ErrorState);
 * a failed tasks fetch only degrades the stats to '—' under an inline
 * apiErrorMessage line — identity cards still render.
 *
 * Cards render ONLY the fields FieldTeamCard names — no salary, no date
 * of birth (the list wire carries both as payroll decoration; the
 * disclosure test pins their absence). Cards are non-clickable (mockup
 * parity) — linking through to the HR record is later polish. "New agent"
 * became "Add Employee": people are created in HR, never duplicated here.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts.
 */
import { useState } from 'react'
import { Mail, MapPin, Phone, Plus } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../DashboardShared'
import { useAsync, fetchDirectory, fetchTasks } from '../../api'
import { accent } from '../../../../theme/tokens'
import { buildFieldTeam, monthWindow, teamCountLine, type FieldTeamCard } from './fieldTeamView'
import { useWriteAccess } from '../../../../lib/writeAccess'

function ProfileCard({ card, statsFailed }: { card: FieldTeamCard; statsFailed: boolean }) {
  const stat = (value: number, label: string) => (
    <div>
      <div className="text-16 font-bold text-ink-primary">{statsFailed ? '—' : value}</div>
      <div className="text-overline uppercase tracking-overline text-ink-muted mt-[1px]">{label}</div>
    </div>
  )
  return (
    // flex-col + mt-auto on the stats row: grid rows stretch cards equal, so
    // stats pin to the bottom edge and align across a row of unequal cards
    // (a PARTIAL trio card has fewer meta rows than a FULL one).
    <div className="k-card p-5 flex flex-col">
      {/* Top: avatar + name block (:1736-1743). The avatar is the Team
          Tracker's initials chip at the mockup's 44px card size. */}
      <div className="flex items-center gap-[14px] mb-[14px]">
        <span className="w-11 h-11 flex items-center justify-center rounded-full text-14 font-bold flex-shrink-0"
          style={{ background: accent.soft, color: accent.DEFAULT }}>
          {card.initials}
        </span>
        <div className="min-w-0">
          <div className="text-14 font-bold text-ink-primary truncate">{card.name}</div>
          {card.jobTitle && <div className="text-12 text-ink-muted mt-[1px] truncate">{card.jobTitle}</div>}
          <div className="text-11 text-ink-muted mt-[2px]">Employee #{card.employeeNumber}</div>
        </div>
      </div>

      {/* Meta rows (:1744-1748) — each omitted when empty, never dashed. */}
      {(card.email || card.phone || card.region) && (
        <div className="flex flex-col gap-[7px] mb-[14px]">
          {card.email && (
            <div className="flex items-center gap-[7px] text-12 text-ink-secondary min-w-0">
              <Mail size={14} className="text-ink-muted flex-shrink-0" /><span className="truncate">{card.email}</span>
            </div>
          )}
          {card.phone && (
            <div className="flex items-center gap-[7px] text-12 text-ink-secondary">
              <Phone size={14} className="text-ink-muted flex-shrink-0" />{card.phone}
            </div>
          )}
          {card.region && (
            <div className="flex items-center gap-[7px] text-12 text-ink-secondary">
              <MapPin size={14} className="text-ink-muted flex-shrink-0" />{card.region}
            </div>
          )}
        </div>
      )}

      {/* Stats + status (:1749-1753). The badge reads the row's real status
          — always Active under the ?status=active population. */}
      <div className="flex items-center gap-[18px] pt-3 mt-auto border-t border-edge-row-faint">
        {stat(card.total, 'Tasks this month')}
        {stat(card.completed, 'Completed')}
        <div className="ml-auto"><StatusBadge status={card.status} /></div>
      </div>
    </div>
  )
}

export function OperationsFieldTeam({ onNavigate }: { onNavigate: (page: string) => void }) {
  // The month window is fixed at mount (the injected-now seam fieldTeamView
  // tests): one windowed fetch, no month nav — the calendar page owns that.
  const [month] = useState(() => monthWindow(new Date()))
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])
  const tasks = useAsync(() => fetchTasks({ from: month.from, to: month.to }), [])
  // "Add Employee" opens the HR create path: offered only to a role that can
  // create employees (records access, lib/writeAccess; fail closed).
  const { records } = useWriteAccess()

  if (employees.loading || tasks.loading) return <LoadingState />
  if (employees.error) return <ErrorState message={employees.error} />

  const cards = buildFieldTeam(employees.data ?? [], tasks.data ?? [])

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Agents"
        subtitle={cards.length === 0 ? 'The people on the field team, with their workload this month.' : teamCountLine(cards.length)}
        action={records ? (
          <button className="k-btn inline-flex items-center gap-2" onClick={() => onNavigate('hr.employees')}>
            <Plus size={14} /> Add Employee
          </button>
        ) : undefined}
      />

      {/* Tasks degrade line — the roster still renders below with '—' stats. */}
      {tasks.error && (
        <div className="text-12 text-ink-muted">
          Couldn't load this month's tasks — {tasks.error}
        </div>
      )}

      {cards.length === 0 ? (
        <div className="k-card">
          <EmptyState
            message="No active employees yet"
            hint="People added in HR appear here with their field workload"
            actionLabel="Add Employee"
            onAction={() => onNavigate('hr.employees')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {cards.map(card => <ProfileCard key={card.id} card={card} statsFailed={Boolean(tasks.error)} />)}
        </div>
      )}
    </div>
  )
}
